import React, { useState, useEffect, useRef } from "react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Upload, Camera, ShieldCheck, Clock, CheckCircle2, XCircle, FileImage } from "lucide-react";

export default function Verify() {
  const { user, refreshUser } = useAuth();
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(user?.verification_status || "unverified");
  const [note, setNote] = useState("");
  const idRef = useRef(null);
  const selfieRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/verification/me");
      setStatus(data.status);
      setNote(data.verification?.note || "");
    } catch { /* noop */ }
  };
  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!idFile || !selfieFile) { toast.error("Please upload both your ID and a selfie."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("id_document", idFile);
      fd.append("selfie", selfieFile);
      await api.post("/verification/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted! Our team will review your documents shortly.");
      setStatus("pending");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const StatusBanner = () => {
    if (status === "verified") return <Banner icon={CheckCircle2} tone="emerald" title="Identity verified" body="You're all set. You can connect to and host hotspots." />;
    if (status === "pending") return <Banner icon={Clock} tone="amber" title="Under review" body="Your documents are being reviewed. This usually takes a few minutes." />;
    if (status === "rejected") return <Banner icon={XCircle} tone="red" title="Verification needs attention" body={note || "Please re-upload clearer documents."} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="mx-auto max-w-2xl px-5 sm:px-8 py-12">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck size={22} /><h1 className="font-display text-3xl font-bold tracking-tight">Identity verification</h1></div>
        <p className="text-muted-foreground mb-8">For everyone's safety, we verify every member's identity before they connect. Your documents are encrypted and reviewed privately.</p>

        <StatusBanner />

        {status !== "verified" && (
          <div className="mt-6 space-y-4">
            <Dropzone label="Step 1 · Government ID" hint="Driver's license or passport (JPG/PNG)" icon={FileImage} file={idFile} onPick={() => idRef.current.click()} testid="id-dropzone" />
            <input ref={idRef} type="file" accept="image/*" className="hidden" data-testid="id-file-input" onChange={(e) => setIdFile(e.target.files[0])} />

            <Dropzone label="Step 2 · Selfie" hint="A clear photo of your face" icon={Camera} file={selfieFile} onPick={() => selfieRef.current.click()} testid="selfie-dropzone" />
            <input ref={selfieRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="selfie-file-input" onChange={(e) => setSelfieFile(e.target.files[0])} />

            <button onClick={submit} disabled={submitting} data-testid="verify-submit"
              className="w-full h-12 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
              {submitting ? "Uploading…" : status === "pending" ? "Re-submit documents" : "Submit for verification"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Banner({ icon: Icon, tone, title, body }) {
  const tones = {
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-500",
    amber: "border-amber-500/40 bg-amber-500/5 text-amber-500",
    red: "border-red-500/40 bg-red-500/5 text-red-500",
  };
  return (
    <div className={`rounded-2xl border p-5 flex items-start gap-3 ${tones[tone]}`} data-testid="verification-status-banner">
      <Icon size={20} className="mt-0.5 shrink-0" />
      <div><p className="font-medium text-foreground">{title}</p><p className="text-sm text-muted-foreground mt-0.5">{body}</p></div>
    </div>
  );
}

function Dropzone({ label, hint, icon: Icon, file, onPick, testid }) {
  return (
    <button type="button" onClick={onPick} data-testid={testid}
      className="w-full rounded-2xl border border-dashed border-border bg-card p-6 flex items-center gap-4 text-left hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors">
      <div className="h-12 w-12 rounded-xl bg-accent grid place-items-center shrink-0">{file ? <CheckCircle2 size={22} className="text-emerald-500" /> : <Icon size={22} />}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-xs text-muted-foreground truncate">{file ? file.name : hint}</p>
      </div>
      <Upload size={18} className="text-muted-foreground shrink-0" />
    </button>
  );
}
